import { isIndexedDbAvailable } from '@/doc-sync/indexeddb-doc-store';

const DB_NAME = 'azabu-pattern-assets';
const DB_VERSION = 1;
const ASSETS = 'assets';

/** A cached image blob. `uploadedAt: null` marks a blob present locally but
 *  not yet confirmed on R2 — the background-upload work-list marker. */
export type AssetRecord = { hash: string; blob: Blob; contentType: string; uploadedAt: number | null };

/** Local-first store of content-addressed image blobs (hash → blob). The
 *  interface is what consumers depend on; IndexedDbAssetCache is the browser
 *  impl, InMemoryAssetCache (test/pattern-asset-helpers.ts) the test double. */
export interface AssetCache {
	get(hash: string): Promise<AssetRecord | null>;
	put(rec: AssetRecord): Promise<void>;
	markUploaded(hash: string, at: number): Promise<void>;
	listUnuploaded(): Promise<string[]>;
	clearAll(): Promise<void>;
}

function requestToPromise<T>(req: IDBRequest<T>): Promise<T> {
	return new Promise((resolve, reject) => {
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
	});
}

function txDone(tx: IDBTransaction): Promise<void> {
	return new Promise((resolve, reject) => {
		tx.oncomplete = () => resolve();
		tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
		tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
	});
}

export function isAssetCacheAvailable(): boolean {
	return isIndexedDbAvailable();
}

export class IndexedDbAssetCache implements AssetCache {
	private dbPromise: Promise<IDBDatabase> | null = null;

	private openDb(): Promise<IDBDatabase> {
		if (this.dbPromise == null) {
			this.dbPromise = new Promise((resolve, reject) => {
				const req = indexedDB.open(DB_NAME, DB_VERSION);
				req.onupgradeneeded = () => {
					const db = req.result;
					if (!db.objectStoreNames.contains(ASSETS)) {
						db.createObjectStore(ASSETS, { keyPath: 'hash' });
					}
				};
				req.onsuccess = () => {
					req.result.onversionchange = () => {
						req.result.close();
						this.dbPromise = null;
					};
					resolve(req.result);
				};
				req.onerror = () => {
					this.dbPromise = null;
					reject(req.error ?? new Error('IndexedDB open failed'));
				};
				req.onblocked = () => {
					this.dbPromise = null;
					reject(new Error('IndexedDB upgrade blocked — close other StitchPress tabs and reload'));
				};
			});
		}
		return this.dbPromise;
	}

	async get(hash: string): Promise<AssetRecord | null> {
		const db = await this.openDb();
		const raw = await requestToPromise(
			db.transaction(ASSETS, 'readonly').objectStore(ASSETS).get(hash) as IDBRequest<AssetRecord | undefined>,
		);
		return raw ?? null;
	}

	async put(rec: AssetRecord): Promise<void> {
		const db = await this.openDb();
		const tx = db.transaction(ASSETS, 'readwrite');
		await requestToPromise(tx.objectStore(ASSETS).put(rec));
		await txDone(tx);
	}

	async markUploaded(hash: string, at: number): Promise<void> {
		const db = await this.openDb();
		const tx = db.transaction(ASSETS, 'readwrite');
		const store = tx.objectStore(ASSETS);
		const rec = await requestToPromise(store.get(hash) as IDBRequest<AssetRecord | undefined>);
		if (rec != null) await requestToPromise(store.put({ ...rec, uploadedAt: at }));
		await txDone(tx);
	}

	async listUnuploaded(): Promise<string[]> {
		const db = await this.openDb();
		const all = await requestToPromise(
			db.transaction(ASSETS, 'readonly').objectStore(ASSETS).getAll() as IDBRequest<AssetRecord[]>,
		);
		return all.filter((r) => r.uploadedAt == null).map((r) => r.hash);
	}

	async clearAll(): Promise<void> {
		const db = await this.openDb();
		const tx = db.transaction(ASSETS, 'readwrite');
		await requestToPromise(tx.objectStore(ASSETS).clear());
		await txDone(tx);
	}
}

/** Fresh IndexedDbAssetCache, or null when IndexedDB is unavailable (private
 *  browsing) — callers degrade to legacy inline-src insert (spec §2). */
export function createAssetCache(): AssetCache | null {
	return isAssetCacheAvailable() ? new IndexedDbAssetCache() : null;
}

let cacheSingleton: AssetCache | null | undefined;

/** Memoized app-wide asset cache (one IndexedDB connection). */
export function getAssetCache(): AssetCache | null {
	if (cacheSingleton === undefined) cacheSingleton = createAssetCache();
	return cacheSingleton;
}

/** Test-only: drop the memoized singleton. */
export function resetAssetCacheForTests(): void {
	cacheSingleton = undefined;
}
